import { Link } from 'react-router-dom';
import { COMPETITORS } from '../data/competitors';
import { KEYWORD_PAGES } from '../data/keywordPages';
import { Mail, MessageCircle, Twitter, Handshake, TrendingUp, MessageSquareText, ShieldCheck } from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';

/**
 * PublicFooter — site-wide footer for marketing AND logged-in app pages.
 *
 * The link cluster here is SEO infrastructure as much as UX. Internal links
 * from every public page to every competitor + keyword landing page builds
 * a dense graph that helps Google understand site structure and distributes
 * authority across the long-tail URLs. Order matters: highest-priority pages
 * are listed first because text closer to the start gets more SEO weight.
 *
 * Premium redesign 2026-06-05:
 *   - Brand strip at top (logo + tagline) so the footer reads as a deliberate
 *     close to the page, not an apologetic afterthought.
 *   - Feedback / suggestions inbox surfaced prominently — the previous footer
 *     listed Partnerships / Investors / Support but had no path for "I have
 *     an idea / complaint / feature request".
 *   - Same footer ships site-wide (logged-in app + public marketing) so the
 *     experience is consistent. The collapsible variant via `compact` prop
 *     hides the SEO link grid on app pages where it would feel out of place.
 */
export default function PublicFooter({ compact = false }) {
  return (
    <footer className="border-t border-dark-700/40 bg-[#070810] text-gray-400">
      <div className="max-w-6xl mx-auto px-6 py-12">

        {/* ── Brand strip ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-6 pb-8 mb-8 border-b border-dark-700/40">
          <Link to="/" className="flex items-center gap-3 group">
            <img src={logo} alt="haythix" className="h-9 w-9 rounded-lg" />
            <div>
              <p className="text-white font-bold text-lg leading-tight group-hover:text-amber-400 transition-colors">haythix</p>
              <p className="text-gray-500 text-xs">Verifiable-methodology crypto trading</p>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              No fake track records
            </span>
            <span className="text-dark-600">·</span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              No mock data
            </span>
            <span className="text-dark-600">·</span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Real numbers only
            </span>
          </div>
        </div>

        {/* ── Contact + community strip ─────────────────────────────────────── */}
        <div className="mb-10 pb-8 border-b border-dark-700/40 grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-white font-bold text-base mb-1">Get in touch</h3>
            <p className="text-gray-500 text-xs mb-4">Real humans, business-hour response. Pick the right inbox so we route your message correctly.</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link to="/contact" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-amber-500/40 transition-colors">
                <Mail className="w-4 h-4 text-amber-400" />
                <span className="text-gray-300">All inboxes</span>
              </Link>
              <a href="mailto:feedback@haythix.com" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-cyan-500/40 transition-colors">
                <MessageSquareText className="w-4 h-4 text-cyan-400" />
                <span className="text-gray-300">Feedback &amp; ideas</span>
              </a>
              <a href="mailto:partnerships@haythix.com" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-emerald-500/40 transition-colors">
                <Handshake className="w-4 h-4 text-emerald-400" />
                <span className="text-gray-300">Partnerships</span>
              </a>
              <a href="mailto:investors@haythix.com" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-violet-500/40 transition-colors">
                <TrendingUp className="w-4 h-4 text-violet-400" />
                <span className="text-gray-300">Investors</span>
              </a>
              <a href="mailto:support@haythix.com" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-amber-500/40 transition-colors col-span-2">
                <Mail className="w-4 h-4 text-amber-400" />
                <span className="text-gray-300">Customer support</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold text-base mb-1">Follow + community</h3>
            <p className="text-gray-500 text-xs mb-4">Updates, market commentary, and a community of traders who care about real numbers.</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <a href="https://x.com/haythix" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 px-3 py-3 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-amber-500/40 transition-colors">
                <Twitter className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-xs font-mono">@haythix</span>
                <span className="text-gray-600 text-[10px]">X</span>
              </a>
              <a href="https://t.me/haythix" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 px-3 py-3 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-blue-500/40 transition-colors">
                <MessageCircle className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300 text-xs font-mono">@haythix</span>
                <span className="text-gray-600 text-[10px]">Telegram channel</span>
              </a>
              <a href="https://t.me/Haythix-Official" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 px-3 py-3 rounded-lg bg-dark-800 border border-dark-700/50 hover:border-violet-500/40 transition-colors">
                <MessageCircle className="w-4 h-4 text-violet-400" />
                <span className="text-gray-300 text-xs font-mono">@Haythix-Official</span>
                <span className="text-gray-600 text-[10px]">Telegram community</span>
              </a>
            </div>
          </div>
        </div>

        {/* ── SEO link grid (hidden on app pages via `compact`) ─────────────── */}
        {!compact && (
          <div className="grid md:grid-cols-4 gap-8">
            {/* Product */}
            <div>
              <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features"   className="hover:text-amber-400 transition-colors">Features</Link></li>
                <li><Link to="/pricing"    className="hover:text-amber-400 transition-colors">Pricing</Link></li>
                <li><Link to="/comparison" className="hover:text-amber-400 transition-colors">Comparison</Link></li>
                <li><Link to="/register"   className="hover:text-amber-400 transition-colors">Free trial</Link></li>
                <li><Link to="/advertise"  className="hover:text-amber-400 transition-colors">Advertise on haythix</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/contact"   className="hover:text-amber-400 transition-colors">Contact</Link></li>
                <li><a href="mailto:partnerships@haythix.com" className="hover:text-amber-400 transition-colors">Partnerships</a></li>
                <li><a href="mailto:investors@haythix.com" className="hover:text-amber-400 transition-colors">Investors</a></li>
                <li><a href="mailto:press@haythix.com" className="hover:text-amber-400 transition-colors">Press inquiries</a></li>
                <li><a href="mailto:feedback@haythix.com" className="hover:text-amber-400 transition-colors">Feedback &amp; suggestions</a></li>
                <li><Link to="/analyze" className="hover:text-amber-400 transition-colors">Token Health Analyzer</Link></li>
                <li><Link to="/wallet"  className="hover:text-amber-400 transition-colors">Wallet Profiler</Link></li>
              </ul>
            </div>

            {/* By topic */}
            <div>
              <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-4">By topic</h3>
              <ul className="space-y-2 text-sm">
                {Object.values(KEYWORD_PAGES).map((p) => (
                  <li key={p.slug}>
                    <Link to={`/${p.slug}`} className="hover:text-amber-400 transition-colors">
                      {p.seoTitle.replace(/ — .*$/, '').replace(/ in 20\d\d$/, '')}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Vs competitors */}
            <div>
              <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-4">Compare</h3>
              <ul className="space-y-2 text-sm">
                {Object.values(COMPETITORS).map((c) => (
                  <li key={c.slug}>
                    <Link to={`/vs/${c.slug}`} className="hover:text-amber-400 transition-colors">
                      haythix vs {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className={`${compact ? 'mt-0' : 'mt-10'} pt-6 border-t border-dark-700/40 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs`}>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-amber-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms"   className="hover:text-amber-400 transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link>
            <a href="mailto:feedback@haythix.com" className="hover:text-amber-400 transition-colors">Send feedback</a>
            <Link to="/docs"    className="hover:text-amber-400 transition-colors">Documentation</Link>
            <Link to="/guides"  className="hover:text-amber-400 transition-colors">Learning Library</Link>
          </div>
          <p className="text-gray-600">© 2026 haythix · Verifiable-methodology crypto trading</p>
        </div>
      </div>
    </footer>
  );
}
